import { REFERENT_NOWHERE, TAG_FILTER_LABEL, versionedDay } from '@/lib/versioned-labels';
import type {
  VersionedCard,
  VersionedFilters,
  VersionedKind,
  VersionedTagFilter,
} from '@matter_extractor/shared';
import { versionedSortSchema, versionedTagFilterSchema } from '@matter_extractor/shared';

/**
 * The rule set §4 gives to the list of EVERY versioned object: thirteen
 * filters, four sorts, the sparse URL they live in, and the chips that undo
 * them.
 *
 * ## Why it is a module and not a component
 *
 * The route serves each library WHOLE - retired entries included - and the page
 * filters. Thirteen query parameters against one small table would buy a round
 * trip per keystroke; here the same dimensions are a pure function of
 * `(cards, filters)`, so every one of them is asserted without a base, without
 * a render and without a network.
 *
 * ## What an object adds, and how
 *
 * A criterion has one filter and one sort of its own (« exemples », §6); a
 * persona has two filters (présentation, cran). None of that belongs here, and
 * none of it can be left out either - a filter the layer did not know about
 * would silently survive a « Réinitialiser » and a chip it could not build
 * would be a filter an operator cannot take off. So each object declares a
 * `VersionedListSpec`: its neutral state, and the five hooks below. The layer
 * calls them and knows nothing else.
 *
 * ## The URL is the state, and it is SPARSE
 *
 * A filtered list has to be collable in a team thread - the régime
 * `/admin/journal?session=` and `/admin/entretiens?reference=` already hold. So
 * the filters ARE the search params, with two properties that make that
 * bearable:
 *
 *   - `readVersionedFilters` NEVER throws. `validateSearch` runs on whatever
 *     somebody typed, and a throw there is a blank screen for a typo. An
 *     unreadable value falls back to its neutral default rather than to an
 *     error.
 *   - `versionedSearchOf` writes only what DIFFERS from the default, so a reset
 *     list has a bare `/admin/criteres` and not thirteen `all` nobody can read.
 */

/* --------------------------------------------------------------- the chips */

/**
 * One active filter, as a removable pill (§4).
 *
 * `patch` is what clicking the cross applies - the neutral value of that one
 * dimension - rather than an id the caller has to switch on. So the chip row
 * knows nothing about the filters it removes, and a filter added to the schema
 * cannot be made unremovable by a forgotten branch.
 */
export interface VersionedFilterChip<Filters> {
  readonly id: string;
  readonly label: string;
  readonly patch: Partial<Filters>;
}

/* ---------------------------------------------------------------- the spec */

/**
 * What one object declares so the layer can filter its list without knowing
 * what it holds.
 *
 * Five hooks and a neutral state. Each hook covers exactly the dimensions the
 * object added: the layer handles the thirteen of §4 itself and never asks
 * about them.
 */
export interface VersionedListSpec<Card extends VersionedCard, Filters extends VersionedFilters> {
  /** The neutral state, every field decided - the object's schema's own defaults. */
  readonly defaults: Filters;
  /** The object's OWN dimensions, read out of a raw search. Sparse, and never throws. */
  readonly read: (search: Record<string, unknown>) => Partial<Filters>;
  /** The same dimensions written back to the URL, neutral ones left out. */
  readonly write: (filters: Filters) => Partial<Filters>;
  /** Does a card survive the object's own dimensions? */
  readonly matches: (card: Card, filters: Filters) => boolean;
  /** The chips of those dimensions, each carrying the patch that removes it. */
  readonly chips: (filters: Filters) => VersionedFilterChip<Filters>[];
  /**
   * The comparators the four common sorts do not cover, keyed by sort value.
   * Ties are broken by name by the layer, so a comparator here returns 0 when
   * its own measure is equal.
   */
  readonly sorts: Readonly<Record<string, (a: Card, b: Card) => number>>;
}

/* ------------------------------------------------------------------ the URL */

/**
 * The address bar's own shape: the filter state with every NEUTRAL value left
 * out.
 *
 * A second type beside the total one, and the split is what makes the URL
 * readable. The total state is what the pure rules take - every field decided.
 * The URL is sparse, because an absent parameter and a neutral filter are the
 * same thing (which is exactly what the schema's defaults say).
 *
 * It is also what TanStack Router derives its `search` type from: all-optional
 * here means a `Link to="/admin/criteres"` needs no search object, which is the
 * difference between a rail entry that compiles and one that does not.
 */
export type VersionedSearch<Filters> = Partial<Filters>;

/** A `YYYY-MM-DD` day, or `undefined`. The bounds of the two period filters. */
function readDay(raw: unknown): string | undefined {
  return typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : undefined;
}

/**
 * A flag, read from every spelling one can arrive in - and the NUMBER is the
 * one that matters.
 *
 * TanStack Router's default `parseSearch` runs `JSON.parse` over each decoded
 * value, so `?showRetired=1` reaches this function as the number `1` and never
 * as the string `'1'`, and `?showRetired=true` as the boolean `true`. Reading
 * strings alone made the hand-written spelling of a flag - the one somebody
 * types into a chat message - silently neutral, which is worse than an error
 * because the screen then answers as though no filter had been asked for.
 *
 * The string forms stay accepted anyway: they are what a caller passing a raw
 * record hands over (the unit tests, and any future serialiser), and accepting
 * both costs one comparison.
 */
function readFlag(raw: unknown): boolean {
  return raw === true || raw === 1 || raw === '1' || raw === 'true' || raw === '';
}

/**
 * Does the object declare a comparator of its own under this name?
 *
 * `Object.hasOwn` and NEVER the `in` operator, and the difference is a crash:
 * `in` walks the prototype chain, so `?sort=valueOf` and `?sort=__proto__`
 * would both answer « known », survive into the URL, and then be CALLED as a
 * comparator - `Object.prototype.valueOf` with no `this`, or an object that is
 * not a function at all. What comes back is the error boundary, on the one path
 * this module promises never to break: a link pasted with a typo.
 */
function hasOwnSort(sorts: Readonly<Record<string, unknown>>, name: string): boolean {
  return Object.hasOwn(sorts, name);
}

/**
 * The chosen sort, or `undefined` when it is the neutral one or unreadable.
 *
 * Validated against the four common values PLUS the keys of the object's own
 * comparators, which is why the spec carries them as a record: the layer then
 * needs no second declaration of « what sorts does this object have », and a
 * sort added without a comparator cannot survive a URL.
 */
function readSort<Card extends VersionedCard, Filters extends VersionedFilters>(
  raw: unknown,
  spec: VersionedListSpec<Card, Filters>,
): Filters['sort'] | undefined {
  if (typeof raw !== 'string' || raw === spec.defaults.sort) return undefined;
  const known =
    hasOwnSort(spec.sorts, raw) || (versionedSortSchema.options as readonly string[]).includes(raw);
  return known ? (raw as Filters['sort']) : undefined;
}

export function readVersionedFilters<Card extends VersionedCard, Filters extends VersionedFilters>(
  search: Record<string, unknown>,
  spec: VersionedListSpec<Card, Filters>,
): VersionedSearch<Filters> {
  /*
    Two records and not one, and the split is the ORDER of the query string: a
    record's keys come out in insertion order, and the object's own dimensions
    are drawn at the head of « Plus de filtres », right after the five visible
    filters. Composed at the end as visible, own, hidden, sort - so a pasted
    link reads in the order of the controls that produced it.

    Both are typed as the COMMON shape and widened once: the thirteen fields are
    the ones `VersionedFilters` declares, so they belong to every object's
    filters - but TypeScript cannot see that through an unresolved type
    parameter, and the cast pays for the generic rather than for anything an
    object could disagree about.
  */
  const visible: Partial<VersionedFilters> = {};
  const hidden: Partial<VersionedFilters> = {};

  const name = typeof search.name === 'string' ? search.name.trim() : '';
  if (name !== '') visible.name = name;

  const tag = versionedTagFilterSchema.safeParse(search.tag);
  if (tag.success && tag.data !== spec.defaults.tag) visible.tag = tag.data;

  /*
    `REFERENT_NOWHERE` is the whole vocabulary of the two referent filters
    today, and the argument is at the constant's own declaration: a card carries
    usage as a count per kind, so an entity id could not be resolved and would
    silently empty the grid. An unknown value is DROPPED rather than kept, so a
    link pasted from a future lot degrades into « pas de filtre » instead of
    into « aucun résultat ».
  */
  if (search.protocolId === REFERENT_NOWHERE) visible.protocolId = REFERENT_NOWHERE;
  if (search.experienceId === REFERENT_NOWHERE) visible.experienceId = REFERENT_NOWHERE;

  if (typeof search.author === 'string' && search.author !== '') visible.author = search.author;

  const createdFrom = readDay(search.createdFrom);
  if (createdFrom !== undefined) hidden.createdFrom = createdFrom;
  const createdTo = readDay(search.createdTo);
  if (createdTo !== undefined) hidden.createdTo = createdTo;
  const versionFrom = readDay(search.versionFrom);
  if (versionFrom !== undefined) hidden.versionFrom = versionFrom;
  const versionTo = readDay(search.versionTo);
  if (versionTo !== undefined) hidden.versionTo = versionTo;

  if (readFlag(search.showRetired)) hidden.showRetired = true;
  if (readFlag(search.unusedOnly)) hidden.unusedOnly = true;
  if (readFlag(search.pendingDraftOnly)) hidden.pendingDraftOnly = true;
  if (readFlag(search.developmentAheadOnly)) hidden.developmentAheadOnly = true;

  const sort = readSort(search.sort, spec);
  return {
    ...(visible as Partial<Filters>),
    ...spec.read(search),
    ...(hidden as Partial<Filters>),
    ...(sort === undefined ? {} : ({ sort } as Partial<Filters>)),
  };
}

/**
 * The total state the pure rules take, defaults filled in for whatever the URL
 * left out - and SANITISED on the way, by the same reader `validateSearch` uses.
 *
 * ## Why it re-reads instead of spreading
 *
 * It used to be `{ ...DEFAULT, ...search }`, and that trusted the router to
 * hand over nothing but validated values. It does not: `useSearch()` on a child
 * route also carries the search params its ANCESTORS never validated, so
 * `?tag=nawak` reached this function untouched, survived the spread, and then
 * passed the `filters.tag !== 'all'` guard of the chip builder - where
 * `TAG_FILTER_LABEL['nawak']` is `undefined` and `.toLowerCase()` threw.
 *
 * The screen that came back was the error boundary, on the one path this page
 * promises never to break: a link pasted with a typo. Caught by the end-to-end
 * test that asserts exactly that promise, and by nothing else - the unit tests
 * passed a well-typed search, which is precisely what the URL is not.
 *
 * So the derivation is TOTAL: whatever reaches it, valid or not, goes through
 * the reader once and comes out inside the enums. The type was already saying
 * so; now it is true.
 */
export function versionedFiltersOf<Card extends VersionedCard, Filters extends VersionedFilters>(
  search: VersionedSearch<Filters>,
  spec: VersionedListSpec<Card, Filters>,
): Filters {
  return { ...spec.defaults, ...readVersionedFilters(search, spec) };
}

/**
 * The other direction: a total state back down to what the URL should carry.
 *
 * The round trip `versionedFiltersOf(readVersionedFilters(versionedSearchOf(f)))`
 * is asserted in the tests, because that loop is what makes a pasted link mean
 * what it showed.
 */
export function versionedSearchOf<Card extends VersionedCard, Filters extends VersionedFilters>(
  filters: Filters,
  spec: VersionedListSpec<Card, Filters>,
): VersionedSearch<Filters> {
  // Same four blocks in the same order as the reader, for the same reason.
  const visible: Partial<VersionedFilters> = {};
  const hidden: Partial<VersionedFilters> = {};
  if (filters.name !== '') visible.name = filters.name;
  if (filters.tag !== spec.defaults.tag) visible.tag = filters.tag;
  if (filters.protocolId !== null) visible.protocolId = filters.protocolId;
  if (filters.experienceId !== null) visible.experienceId = filters.experienceId;
  if (filters.author !== null) visible.author = filters.author;
  if (filters.createdFrom !== null) hidden.createdFrom = filters.createdFrom;
  if (filters.createdTo !== null) hidden.createdTo = filters.createdTo;
  if (filters.versionFrom !== null) hidden.versionFrom = filters.versionFrom;
  if (filters.versionTo !== null) hidden.versionTo = filters.versionTo;
  if (filters.showRetired) hidden.showRetired = true;
  if (filters.unusedOnly) hidden.unusedOnly = true;
  if (filters.pendingDraftOnly) hidden.pendingDraftOnly = true;
  if (filters.developmentAheadOnly) hidden.developmentAheadOnly = true;
  return {
    ...(visible as Partial<Filters>),
    ...spec.write(filters),
    ...(hidden as Partial<Filters>),
    ...(filters.sort === spec.defaults.sort ? {} : ({ sort: filters.sort } as Partial<Filters>)),
  };
}

/* ------------------------------------------------------------ the filtering */

/** The `YYYY-MM-DD` day an ISO stamp falls on, LOCAL, so it matches a date input. */
function dayOf(stamp: string): string {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const month = String(at.getMonth() + 1).padStart(2, '0');
  return `${at.getFullYear()}-${month}-${String(at.getDate()).padStart(2, '0')}`;
}

/**
 * A closed period, either bound optional. Both bounds are INCLUSIVE: an
 * operator who types the same day twice means that day, and a half-open range
 * would answer with nothing.
 */
function withinPeriod(stamp: string, from: string | null, to: string | null): boolean {
  const day = dayOf(stamp);
  if (day === '') return false;
  if (from !== null && day < from) return false;
  if (to !== null && day > to) return false;
  return true;
}

/** How many referents cite an entity, across every kind. */
export function usageTotal(card: VersionedCard): number {
  return card.usage.reduce((total, entry) => total + entry.count, 0);
}

function citedBy(card: VersionedCard, kind: VersionedKind): boolean {
  return card.usage.some((entry) => entry.kind === kind);
}

/**
 * The tag filter (§4): « en production », « jamais passé en production ».
 *
 * Two states and not three, and `versionedTagFilterSchema` says why: every
 * entity has a development pointer by construction, so « en développement »
 * would match everything and teach an operator that the filter is broken.
 */
function matchesTag(card: VersionedCard, tag: VersionedTagFilter): boolean {
  switch (tag) {
    case 'all':
      return true;
    case 'in-production':
      return card.production !== null;
    case 'never-production':
      return card.production === null;
  }
}

/**
 * Does one card survive the retirement rule alone?
 *
 * Split out because the COUNT of « 2 critères sur 6 » is measured against it:
 * the six is the library an operator is currently looking at - retired hidden
 * unless the box is ticked - and not the row count of the table. A total that
 * counted the retired one would make « sur 6 » disagree with the list the
 * moment the box was ticked.
 */
function visibleAtAll(card: VersionedCard, filters: VersionedFilters): boolean {
  return filters.showRetired || card.retiredAt === null;
}

function matches<Card extends VersionedCard, Filters extends VersionedFilters>(
  card: Card,
  filters: Filters,
  spec: VersionedListSpec<Card, Filters>,
): boolean {
  if (!visibleAtAll(card, filters)) return false;
  // Accent-insensitive would need a normalisation the schema does not promise;
  // case-insensitive on a substring is what a name search is, and it is what
  // `name-taken` compares on the server side of the unicity.
  if (filters.name !== '' && !card.name.toLowerCase().includes(filters.name.toLowerCase())) {
    return false;
  }
  if (!matchesTag(card, filters.tag)) return false;
  if (filters.protocolId === REFERENT_NOWHERE && citedBy(card, 'protocol')) return false;
  if (filters.experienceId === REFERENT_NOWHERE && citedBy(card, 'experience')) return false;
  // « a créé OU a écrit une version » is ONE filter, because a v1 is a
  // creation: `authors` carries the creator plus every version's author.
  if (filters.author !== null && !card.authors.includes(filters.author)) return false;
  if (!withinPeriod(card.createdAt, filters.createdFrom, filters.createdTo)) return false;
  if (!withinPeriod(card.lastVersionAt, filters.versionFrom, filters.versionTo)) return false;
  if (filters.unusedOnly && card.usage.length > 0) return false;
  if (filters.pendingDraftOnly && !card.hasPendingDraft) return false;
  if (filters.developmentAheadOnly && !card.developmentAhead) return false;
  return spec.matches(card, filters);
}

/* --------------------------------------------------------------- the sorting */

/**
 * The four common sorts of §4 and the object's own, every one of them
 * tie-broken BY NAME.
 *
 * The tie-break is what makes the grid stable: `lastVersionAt` is a second-
 * resolution stamp, so two entities saved in the same minute would swap places
 * between two renders of the same data, and a card that moves without anything
 * changing reads as a bug. `localeCompare` with an explicit `'fr'` rather than
 * the runner's locale, for the reason `versionedDay` gives: a test must assert
 * the same order on every machine.
 */
function byName(a: VersionedCard, b: VersionedCard): number {
  return a.name.localeCompare(b.name, 'fr', { sensitivity: 'base' });
}

function compare<Card extends VersionedCard, Filters extends VersionedFilters>(
  a: Card,
  b: Card,
  filters: Filters,
  spec: VersionedListSpec<Card, Filters>,
): number {
  switch (filters.sort) {
    case 'name':
      // The default of every list (§4, issue #113).
      return byName(a, b);
    case 'last-version':
      // Most recent first.
      return b.lastVersionAt.localeCompare(a.lastVersionAt) || byName(a, b);
    case 'created':
      return b.createdAt.localeCompare(a.createdAt) || byName(a, b);
    case 'usage':
      return usageTotal(b) - usageTotal(a) || byName(a, b);
    default: {
      /*
        The object's own sorts. An unknown value cannot reach here from the URL
        - `readSort` drops it - but a caller passing a raw record can, and
        falling back to the name keeps the grid ordered rather than arbitrary.

        `hasOwnSort` and not a bare lookup, for the reason it states at its own
        declaration: `spec.sorts['valueOf']` finds `Object.prototype.valueOf`,
        which is not a comparator and throws when called.
      */
      if (!hasOwnSort(spec.sorts, filters.sort)) return byName(a, b);
      const own = spec.sorts[filters.sort];
      return own === undefined ? byName(a, b) : own(a, b) || byName(a, b);
    }
  }
}

/**
 * The list as the grid shows it: filtered, then sorted.
 *
 * A COPY is sorted and never the argument - `Array.prototype.sort` mutates,
 * and the argument here is the array TanStack Query holds in its cache. A
 * sort in place would reorder the cache under whatever else is reading it.
 */
export function filterVersioned<Card extends VersionedCard, Filters extends VersionedFilters>(
  cards: readonly Card[],
  filters: Filters,
  spec: VersionedListSpec<Card, Filters>,
): Card[] {
  return cards
    .filter((card) => matches(card, filters, spec))
    .sort((a, b) => compare(a, b, filters, spec));
}

/* ------------------------------------------------------------- the counting */

export interface VersionedCounts {
  /** How many cards the grid shows. */
  readonly shown: number;
  /** How many the library holds under the retirement rule alone - the « sur 6 ». */
  readonly total: number;
  /** How many are retired, for the « Afficher les retirés (1) » hint. */
  readonly retired: number;
}

export function versionedCounts<Card extends VersionedCard, Filters extends VersionedFilters>(
  cards: readonly Card[],
  filters: Filters,
  spec: VersionedListSpec<Card, Filters>,
): VersionedCounts {
  return {
    shown: cards.filter((card) => matches(card, filters, spec)).length,
    total: cards.filter((card) => visibleAtAll(card, filters)).length,
    retired: cards.filter((card) => card.retiredAt !== null).length,
  };
}

/** « 6 critères » with nothing filtered, « 2 critères sur 6 » with something (§4). */
export function versionedCountLabel(
  counts: VersionedCounts,
  noun: readonly [string, string],
): string {
  const [singular, plural] = noun;
  const word = counts.shown === 1 ? singular : plural;
  return counts.shown === counts.total
    ? `${counts.shown} ${word}`
    : `${counts.shown} ${word} sur ${counts.total}`;
}

/**
 * Every author the list offers, deduplicated and sorted - what the select holds.
 *
 * Measured against the RETIREMENT RULE and not against the whole table, which
 * is the same honesty `REFERENT_NOWHERE` argues for at its own declaration: an
 * author whose only entity is retired would otherwise sit in the select and
 * answer with an empty grid, teaching an operator that the filter is broken.
 * Ticking « Afficher les retirés » puts them back, because then their entity is
 * on screen.
 */
export function versionedAuthors(
  cards: readonly VersionedCard[],
  filters: VersionedFilters,
): string[] {
  return [
    ...new Set(cards.filter((card) => visibleAtAll(card, filters)).flatMap((card) => card.authors)),
  ].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
}

/* ----------------------------------------------------------------- the chips */

/** « du 1 sept. 2026 au 9 sept. 2026 », « depuis le ... », « jusqu'au ... ». */
function periodLabel(from: string | null, to: string | null): string {
  if (from !== null && to !== null) return `du ${versionedDay(from)} au ${versionedDay(to)}`;
  if (from !== null) return `depuis le ${versionedDay(from)}`;
  return `jusqu’au ${versionedDay(to ?? '')}`;
}

/**
 * The chips of the thirteen common dimensions, then the object's own.
 *
 * The order is the order of the filter row and of the panel, so the chips read
 * left to right as the controls do. An object's chips come last because its
 * controls sit at the head of « Plus de filtres » - a criterion's « Exemples »
 * is drawn after the five visible filters, and its chip lands after theirs.
 */
export function activeVersionedFilters<
  Card extends VersionedCard,
  Filters extends VersionedFilters,
>(filters: Filters, spec: VersionedListSpec<Card, Filters>): VersionedFilterChip<Filters>[] {
  const chips: VersionedFilterChip<Filters>[] = [];
  const push = (id: string, label: string, patch: Partial<VersionedFilters>) =>
    chips.push({ id, label, patch: patch as Partial<Filters> });

  if (filters.name !== '') push('name', `Nom : ${filters.name}`, { name: '' });
  if (filters.tag !== 'all') {
    push('tag', `Tag : ${TAG_FILTER_LABEL[filters.tag].toLowerCase()}`, { tag: 'all' });
  }
  if (filters.protocolId !== null)
    push('protocolId', 'Protocole : nulle part', { protocolId: null });
  if (filters.experienceId !== null) {
    push('experienceId', 'Expérience : nulle part', { experienceId: null });
  }
  if (filters.author !== null) push('author', `Auteur : ${filters.author}`, { author: null });
  // The object's own dimensions, between the five visible filters and the
  // periods, exactly where its controls are drawn.
  chips.push(...spec.chips(filters));
  if (filters.createdFrom !== null || filters.createdTo !== null) {
    push('created', `Créé ${periodLabel(filters.createdFrom, filters.createdTo)}`, {
      createdFrom: null,
      createdTo: null,
    });
  }
  if (filters.versionFrom !== null || filters.versionTo !== null) {
    push('version', `Dernière version ${periodLabel(filters.versionFrom, filters.versionTo)}`, {
      versionFrom: null,
      versionTo: null,
    });
  }
  if (filters.showRetired) push('showRetired', 'Retirés affichés', { showRetired: false });
  if (filters.unusedOnly) push('unusedOnly', 'Utilisé nulle part', { unusedOnly: false });
  if (filters.pendingDraftOnly) {
    push('pendingDraftOnly', 'Brouillon en attente', { pendingDraftOnly: false });
  }
  if (filters.developmentAheadOnly) {
    push('developmentAheadOnly', 'Latest dev en avance sur latest prod', {
      developmentAheadOnly: false,
    });
  }
  return chips;
}

/**
 * Is anything filtered? The SORT does not count, and that is deliberate: it
 * reorders the list and hides nothing, so « Réinitialiser les filtres » leaving
 * a chosen sort in place is what an operator expects.
 */
export function hasActiveVersionedFilters<
  Card extends VersionedCard,
  Filters extends VersionedFilters,
>(filters: Filters, spec: VersionedListSpec<Card, Filters>): boolean {
  return activeVersionedFilters(filters, spec).length > 0;
}

/**
 * Is one of the filters that live BEHIND « Plus de filtres » armed? (§4)
 *
 * What opens the panel on arrival, and only then: a pasted link carries the
 * filters that shaped what somebody saw, so a hidden one has to be visible or
 * the list looks wrong for no stated reason. The object's OWN dimensions count
 * as hidden - §4 keeps the five common ones in the row and everything else in
 * the panel - which is exactly what its chips say.
 */
export function hasHiddenVersionedFilter<
  Card extends VersionedCard,
  Filters extends VersionedFilters,
>(filters: Filters, spec: VersionedListSpec<Card, Filters>): boolean {
  return (
    spec.chips(filters).length > 0 ||
    filters.createdFrom !== null ||
    filters.createdTo !== null ||
    filters.versionFrom !== null ||
    filters.versionTo !== null ||
    filters.showRetired ||
    filters.unusedOnly ||
    filters.pendingDraftOnly ||
    filters.developmentAheadOnly
  );
}

/**
 * « Réinitialiser les filtres » - the neutral state, WITH the chosen sort kept.
 *
 * The sort is not a filter: it reorders and hides nothing, so resetting it
 * would undo a choice the operator did not make by accident (§4).
 */
export function resetVersionedFilters<Card extends VersionedCard, Filters extends VersionedFilters>(
  filters: Filters,
  spec: VersionedListSpec<Card, Filters>,
): Filters {
  return { ...spec.defaults, sort: filters.sort };
}
